import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';

import type { UseFormRegisterReturn } from 'react-hook-form';

interface AutoResizeTextareaProps {
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
}

const DEFAULT_MIN_HEIGHT = '44px';

/**
 * 🔴 JS 상한과 CSS 상한은 **같은 값이어야 한다.** 예전에는 JS 가 `?? '600'`, CSS 가 `?? '500px'` 로
 * 갈려 있었다. 그 사이(500~600px) 구간에서는 JS 가 "상한 미만"으로 보고 `overflowY: hidden` 을
 * 유지하는데 CSS 는 500px 에서 잘라버려, **넘친 내용이 보이지도 않고 스크롤도 안 되는** 상태가 됐다.
 */
const DEFAULT_MAX_HEIGHT = '500px';

/**
 * 콘텐츠 높이 → 적용할 높이·오버플로. **순수 함수**라 DOM 없이 단위 테스트된다. (TRAD-2395)
 *
 * 상한을 넘으면 상한에서 멈추고 스크롤을 연다. 넘지 않으면 콘텐츠 높이 그대로 두고 스크롤을 닫는다
 * (스크롤바가 깜빡이지 않게).
 */
export const resolveAutoResizeHeight = (
  { scrollHeight, maxHeight }: { scrollHeight: number; maxHeight: number },
): { height: string; overflowY: 'auto' | 'hidden' } => (
  scrollHeight > maxHeight
    ? { height: `${maxHeight}px`, overflowY: 'auto' }
    : { height: `${scrollHeight}px`, overflowY: 'hidden' }
);

export const AutoResizeTextarea = forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps & UseFormRegisterReturn>(
  (props, ref) => {
    const { onChange: rhfOnChange, minHeight, maxHeight, placeholder, ...rest } = props;

    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    /**
     * 마지막으로 **높이를 재어 본 값**. 렌더마다 현재 값과 비교해, 달라졌을 때만 DOM 을 만진다
     * (매 렌더 강제 리플로를 피한다). `null` 이면 아직 한 번도 재지 않았다는 뜻.
     */
    const measuredValueRef = useRef<string | null>(null);

    const applyHeight = useCallback((el: HTMLTextAreaElement) => {
      // 먼저 auto 로 풀어야 scrollHeight 가 "지금 내용에 필요한 높이"를 준다.
      // 남아 있는 이전 height 를 그대로 두면 줄어들 때 다시 작아지지 않는다.
      el.style.height = 'auto';

      const { height, overflowY } = resolveAutoResizeHeight({
        scrollHeight: el.scrollHeight,
        maxHeight: parseInt(maxHeight ?? DEFAULT_MAX_HEIGHT, 10),
      });

      el.style.height = height;
      el.style.overflowY = overflowY;
      measuredValueRef.current = el.value;
    }, [maxHeight]);

    /**
     * 🔴 **값이 코드로 주입될 때도 다시 잰다.** (TRAD-2395)
     *
     * 이전에는 `onChange` 에서만 높이를 쟀다. 그런데 게시글 수정 진입은 react-hook-form 의
     * `defaultValues`/`reset()` 으로 값을 넣기 때문에 **change 이벤트가 발생하지 않는다.**
     * 그래서 본문이 아무리 길어도 에디터가 브라우저 기본 높이로 열렸고(실측: 내용 161px 인데
     * 72px 로 열려 89px 이 잘림), 사용자가 아무 글자나 한 번 쳐야 비로소 펼쳐졌다.
     *
     * 의존성 배열을 두지 않아 **매 렌더 후** 확인한다 — 값이 비동기로 도착하는 화면
     * (`reset()` 을 데이터 로드 뒤에 부르는 저널 작성기 등)까지 덮기 위해서다. 실제로 DOM 을
     * 만지는 것은 값이 마지막 측정과 다를 때뿐이다.
     */
    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      if (el.value === measuredValueRef.current) return;

      applyHeight(el);
    });

    const handleInput = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        applyHeight(e.target);
        rhfOnChange?.(e);
      },
      [applyHeight, rhfOnChange],
    );

    /** forwardRef 로 받은 ref(= react-hook-form 의 register ref)와 내부 ref 를 함께 채운다. */
    const setRefs = useCallback((el: HTMLTextAreaElement | null) => {
      innerRef.current = el;

      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    }, [ref]);

    return (
      <textarea
        {...rest}
        ref={setRefs}
        onChange={handleInput}
        placeholder={placeholder}
        className="bg-card text-sm text-foreground w-full rounded-lg border border-border p-3 leading-relaxed resize-none shadow-sm focus:border-primary focus:outline-none transition-colors"
        style={{
          minHeight: minHeight ?? DEFAULT_MIN_HEIGHT,
          maxHeight: maxHeight ?? DEFAULT_MAX_HEIGHT,
        }}
      />
    );
  },
);
